import { Group, GroupProps, Menu, MantineSpacing, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { Fragment, ReactNode } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type AddNewMenuItem = { value: string; label: string };
type AddNewMenuGroup = { label: string; items: AddNewMenuItem[] };
export type AddNewMenuEntry = AddNewMenuItem | AddNewMenuGroup;

export type AddNewAction = {
  label: string;
  testId?: string;
  ariaLabel?: string;
  onClick?: () => void;
  menu?: AddNewMenuEntry[];
  onSelect?: (value: string) => void;
};

type AddNewBarProps = {
  label?: string;
  actions: AddNewAction[];
  py?: MantineSpacing;
  wrap?: GroupProps['wrap'];
};

export default function AddNewBar({
  label = 'Add new:',
  actions,
  py = 'xs',
  wrap,
}: AddNewBarProps) {
  return (
    <Group
      bg="gray.0"
      px="lg"
      py={py}
      gap="xs"
      align="center"
      wrap={wrap}
      style={{ borderRadius: 20 }}
    >
      <Text fw={FONT_WEIGHTS.medium} size="sm" c="gray.4">
        {label}
      </Text>
      <Group gap="lg" align="center" wrap={wrap}>
        {actions.map(action => (
          <AddNewAction key={action.label} action={action} />
        ))}
      </Group>
    </Group>
  );
}

function AddNewAction({ action }: { action: AddNewAction }) {
  const { label, testId, ariaLabel, onClick, menu, onSelect } = action;
  const isDropdown = !!menu;

  if (isDropdown && onSelect) {
    return (
      <Menu position="bottom-start" withinPortal shadow="md">
        <Menu.Target>
          <UnstyledButton data-testid={testId} aria-label={ariaLabel}>
            <ActionLabel label={label} withChevron />
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          {menu.map(entry =>
            isMenuGroup(entry) ? (
              <Fragment key={entry.label}>
                <Menu.Label>{entry.label}</Menu.Label>
                {entry.items.map(item => (
                  <Menu.Item key={item.value} onClick={() => onSelect(item.value)}>
                    {item.label}
                  </Menu.Item>
                ))}
              </Fragment>
            ) : (
              <Menu.Item key={entry.value} onClick={() => onSelect(entry.value)}>
                {entry.label}
              </Menu.Item>
            ),
          )}
        </Menu.Dropdown>
      </Menu>
    );
  }

  if (onClick) {
    return (
      <UnstyledButton onClick={onClick} data-testid={testId} aria-label={ariaLabel}>
        <ActionLabel label={label} withChevron={isDropdown} />
      </UnstyledButton>
    );
  }

  return <ActionLabel label={label} withChevron={isDropdown} />;
}

function ActionLabel({ label, withChevron }: { label: string; withChevron?: boolean }): ReactNode {
  return (
    <Group gap={0} wrap="nowrap">
      <Text fw={FONT_WEIGHTS.semibold} size="sm" c="brand.6">
        {label}
      </Text>
      {withChevron && (
        <IconChevronDown size={20} color="var(--mantine-color-brand-6)" style={{ marginLeft: 8 }} />
      )}
    </Group>
  );
}

function isMenuGroup(entry: AddNewMenuEntry): entry is AddNewMenuGroup {
  return 'items' in entry;
}
